import {
  Button,
  Drawer,
  DrawerPosition,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useEffect, useRef, useState } from 'react'

import { notificationHandler } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useBasket } from '@/providers/BasketProvider'

import BasketLines from './BasketLines'
import BasketSummary from './BasketSummary'

interface Props {
  opened: boolean
  onClose: () => void
  position: DrawerPosition
  handleBasketCount: (count: number) => void
}

export default function BasketDrawer({ opened, onClose, position, handleBasketCount }: Props) {
  // Hooks
  const { isAuthenticated } = useAuth() || {}
  const { basket, basketLoading, basketMutate, cleanBasket } = useBasket() || {}
  const viewport = useRef(null)
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)

  // States
  const [loading, setLoading] = useState<boolean>(false)
  const [modalOpened, setModalOpened] = useState<boolean>(false)
  const [studentUid, setStudentUid] = useState<string>('')
  const [studentName, setStudentName] = useState<string>('')

  // Actions
  const handleCleanBasket = (uid: string, name: string) => {
    setStudentUid(uid)
    setStudentName(name)
    setModalOpened(true)
  }

  const handleConfirmCleanBasket = async () => {
    setLoading(true)
    try {
      const response = await cleanBasket({ studentUid })
      if (response?.status === 200) {
        notificationHandler({ variant: 'success', message: 'O carrinho foi limpado com sucesso.' })
        setModalOpened(false)
        basketMutate?.()
      }
    } catch (error) {
      notificationHandler({
        variant: 'error',
        message: 'Ocorreu um erro ao limpar o carrinho. Tente novamente mais tarde. 🤥'
      })
    }
    setLoading(false)
  }

  // Effects
  useEffect(() => {
    handleBasketCount(basket?.lines?.length || 0)
  }, [basket?.lines?.length, handleBasketCount])

  if (!isAuthenticated) return null

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        padding="lg"
        size={isXs ? 'full' : '70%'}
        title={<Title order={3}>Carrinho</Title>}
        position={position}>
        <ScrollArea
          style={{ height: 'calc(100vh - 100px)' }}
          viewportRef={viewport}
          offsetScrollbars>
          <Grid mr="xs">
            <Grid.Col span={12} lg={8} sx={{ position: 'relative' }}>
              {basketLoading ? (
                <LoadingOverlay overlayColor="white" visible />
              ) : (
                <BasketLines
                  basket={basket}
                  handleCleanBasket={handleCleanBasket}
                  handleMutate={() => basketMutate?.()}
                />
              )}
            </Grid.Col>
            <Grid.Col span={12} lg={4}>
              <BasketSummary basket={basket} />
            </Grid.Col>
          </Grid>
        </ScrollArea>
      </Drawer>
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={<Text weight="bold">Limpar este carrinho</Text>}
        closeButtonLabel="Fechar"
        withCloseButton
        centered>
        <Text mb="sm">
          Tem certeza que deseja remover todos os itens do carrinho
          {studentName && (
            <>
              {' '}
              do aluno{' '}
              <Text component="span" weight="bold">
                {studentName}
              </Text>
            </>
          )}
          ?
        </Text>
        <Group position="right">
          <Button variant="outline" onClick={() => setModalOpened(false)}>
            Fechar
          </Button>
          <Button loading={loading} onClick={handleConfirmCleanBasket}>
            Confirmar
          </Button>
        </Group>
      </Modal>
    </>
  )
}
